// Event names for CoScientist usage tracking. Keep identical to the Electron
// client's names since both post into the same Redshift `arbitrary_super_events` table.
export const EVENTS = {
  APP_LAUNCHED: 'app.launched',
  APP_FIRST_LAUNCH: 'app.first_launch',
  APP_HEARTBEAT: 'app.heartbeat',
  CHAT_THREAD_CREATED: 'chat.thread_created',
  CHAT_VIEWED: 'chat.viewed', // fires on every path into a chat, not just SIDEBAR_CHAT_CLICK
  CHAT_RENAMED: 'chat.renamed',
  CHAT_MESSAGE_SENT: 'chat.message_sent',
  CHAT_FIRST_MESSAGE_SENT: 'chat.first_message_sent',
  CHAT_MESSAGE_RECEIVED: 'chat.message_received',
  // Catches turns stuck 'pending' (sent, no SSE) that running===true recovery misses
  CHAT_TURN_STALE_PENDING: 'chat.turn_stale_pending',
  TOOL_OPENED: 'tool.opened',
  TOOL_IFRAME_READY: 'tool.iframe_ready',
  TOOL_IFRAME_TOKEN_FAILED: 'tool.iframe_token_failed',
  TOOL_IFRAME_KEY_REFRESH_FAILED: 'tool.iframe_key_refresh_failed',
  TOOL_CREATED: 'tool.created',
  TOOL_RENAMED: 'tool.renamed', // not the agent's rename_mini_application tool call
  TOOL_RATED: 'tool.rated',
  TOOL_RATING_DISMISSED: 'tool.rating_dismissed',
  TOOL_FEEDBACK_PROMPT_CLICK: 'tool.feedback_prompt_click',
  TOOL_FEEDBACK_MODAL_DISMISSED: 'tool.feedback_modal_dismissed',
  TOOL_FEEDBACK_SUBMITTED: 'tool.feedback_submitted',
  TOOL_FEEDBACK_THANKS_DISMISSED: 'tool.feedback_thanks_dismissed',
  MOBILE_PANE_SWITCHED: 'mobile.pane_switched',
  // the download button on a paper flow's phone paper bar
  MOBILE_PAPER_BAR_DOWNLOAD: 'mobile.paper_bar_download',
  // Switches inside the tool pane's own tabs, distinct from MOBILE_PANE_SWITCHED above
  PANE_SECTION_SWITCHED: 'pane.section_switched',
  TOOL_EXPORTED: 'tool.exported',
  APP_DOWNLOADED: 'app.downloaded',
  DESKTOP_APP_OPENED: 'desktop_app.opened',
  DESKTOP_APP_OPEN_FAILED: 'desktop_app.open_failed',
  DESKTOP_APP_DOWNLOAD_CLICKED: 'desktop_app.download_clicked',
  DESKTOP_APP_DOWNLOAD_FAILED: 'desktop_app.download_failed',
  TOOL_IMPORTED: 'tool.imported',
  ONBOARDING_STARTED: 'onboarding.started',
  // Every landing on an emailed suggestion, resumes included; onboarding.started is new-only
  ONBOARDING_SUGGESTION_OPENED: 'onboarding.suggestion_opened',
  ONBOARDING_COMPLETED: 'onboarding.completed',
  ONBOARDING_CHOICE_CLICK: 'onboarding.choice_click',
  // COS-685: Try again on classic onboarding's "Couldn't set up your workspace" screen
  ONBOARDING_START_RETRY_CLICK: 'onboarding.start_retry_click',
  // Web-only: the onboarding task card's profile sync Details toggle, with `open`
  ONBOARDING_PROFILE_SYNC_DETAILS_TOGGLED:
    'onboarding.profile_sync_details_toggled',
  // starter_tasks flow: the three starter cards, once per mount when they're ready
  ONBOARDING_STARTER_TASKS_SHOWN: 'onboarding.starter_tasks_shown',
  // A starter card's View results or Follow progress, discriminated by `action` (open | follow)
  ONBOARDING_STARTER_TASK_CARD_CLICK: 'onboarding.starter_task_card_click',
  // The Personalize card, once per mount, with its `state` and `surface`
  ONBOARDING_PERSONALIZE_SHOWN: 'onboarding.personalize_shown',
  // Any control on the Personalize card, discriminated by `action`
  ONBOARDING_PERSONALIZE_CLICK: 'onboarding.personalize_click',
  // Each autosave of the card's answers
  ONBOARDING_PERSONALIZE_SAVED: 'onboarding.personalize_saved',
  // The three ideas written from a save, once per save when they're ready or have failed
  ONBOARDING_PERSONALIZED_SUGGESTIONS_SHOWN:
    'onboarding.personalized_suggestions_shown',
  // An idea's Start, Follow progress or View results, discriminated by `action` (start | follow | open)
  ONBOARDING_PERSONALIZED_SUGGESTION_CLICK:
    'onboarding.personalized_suggestion_click',
  // outcome is SuggestionDeepLinkRouter's, or focus | focus_missing for a plan card user's Home landing
  REENGAGEMENT_LANDING: 'reengagement.landing',

  AGENT_WARMUP_DEGRADED: 'agent.warmup_degraded',
  AGENT_WARMUP_READY: 'agent.warmup_ready',
  // P-L4: per-phase breakdown of the AGENT_WARMUP_READY landing-to-ready window
  AGENT_CONNECT_CHECKPOINT: 'agent.connect_checkpoint',
  AGENT_ENABLE_BUTTON_SHOWN: 'agent.enable_button_shown',
  // COS-640: Retry on a failed agent start, discriminated by `source` (banner | thread); the
  // thread's also carries `start_failure` (agent | session: the server refused the chat, COS-685)
  AGENT_START_RETRY_CLICK: 'agent.start_retry_click',
  // Web-only (COS-847): every AgentStatusProvider transition; a remount shows as from → unknown
  AGENT_STATUS_CHANGED: 'agent.status_changed',
  // Web-only (COS-847): one send's path from click to the agent, joined on the click's send_id
  CHAT_SEND_STEP: 'chat.send_step',
  CHAT_SEND_STALLED: 'chat.send_stalled',
  // Web-only (COS-847): the onboarding pages' background start, by `step`, `trigger` and `mount_id`
  ONBOARDING_START_STEP: 'onboarding.start_step',

  TOOL_BUILD_STARTED: 'tool.build_started',
  TOOL_VIEW: 'tool.view', // tool_creation_source here is distinct from TOOL_RATED's tool_type
  TOOL_NOTIFICATION_VIEW: 'tool.notification_view',
  TOOL_NOTIFICATION_CLICK: 'tool.notification_click',
  TASK_SUGGESTIONS_NOTIFICATION_CLICK: 'task_suggestions_notification_click',
  GRANT_EVAL_NOTIFICATION_CLICK: 'grant_eval_notification_click',
  TASK_COMPLETED_NOTIFICATION_CLICK: 'task_completed_notification_click',
  STARTER_TASKS_COMPLETED_NOTIFICATION_CLICK:
    'starter_tasks_completed_notification_click',
  NOTIFICATION_CLICK: 'notification_click',
  // Browser-side echo of the agent server's task.started/task.completed (the true denominator)
  TASK_RUN_STARTED_RECEIVED: 'task.started_received',
  TASK_RUN_COMPLETED_RECEIVED: 'task.completed_received',
  // Join to its task row on task_id, not chat_session_id — one chat reopens many times
  TASK_SESSION_VIEWED: 'task.session_viewed',
  TOOL_ERROR_VIEWED: 'tool.error_viewed',
  TOOL_CLICK: 'tool.click',
  TOOL_KEYBOARD_EVENT: 'tool.keyboard_event',
  TOOL_RUN_STARTED: 'tool.run_started',
  TOOL_RUN_COMPLETE: 'tool.run_complete',
  TOOL_ERROR_SHOWN: 'tool.error_shown',
  TOOL_ERROR_FIX_CLICKED: 'tool.error_fix_clicked',
  // These connectivity blips are suppressed from TOOL_ERROR_SHOWN; this pair is the only signal
  TOOL_RECONNECT_SHOWN: 'tool.reconnect_shown',
  TOOL_RECONNECT_RECOVERED: 'tool.reconnect_recovered',
  // Payloads are structure-only (extension, size, key names) — never filenames or values
  TOOL_DATA_UPLOADED: 'tool.data_uploaded',
  TOOL_OUTPUT_DOWNLOADED: 'tool.output_downloaded',
  TOOL_PARAMS_CHANGED: 'tool.params_changed',
  TOOL_COACHMARK_COMPLETE: 'tool.coachmark_complete',

  CHAT_SESSION_INPUT_CLICK: 'chat.session_input_click',
  CHAT_SESSION_SEND_CLICK: 'chat.session_send_click',
  CHAT_SESSION_KEYBOARD_EVENT: 'chat.session_keyboard_event',

  SAVE_TOOL_BUTTON_VIEW: 'mini_app.save_tool_button_view',
  SAVE_TOOL_CLICK: 'mini_app.save_tool_click',
  SAVE_TOOL_MODAL_VIEW: 'mini_app.save_tool_modal_view',
  SAVE_TOOL_INSTALL_CLICK: 'mini_app.save_tool_install_click',
  SAVE_TOOL_MODAL_DISMISSED: 'mini_app.save_tool_modal_dismissed',
  SOFTWARE_INSTALL_SEND_TO_AGENT_CLICK:
    'mini_app.software_install_send_to_agent_click',

  EXTENSION_INSTALL_CLICK: 'extension.install_button_click',
  // Fired by the extension's background worker (academia-browser-extensions), not here
  EXTENSION_INSTALLED: 'extension.installed',
  EXTENSION_OVERLAY_OPENED: 'extension.overlay_opened',
  WIDGET_INTERACTION: 'widget.interaction',
  EXTENSION_OVERLAY_TOOL_CLICK: 'extension.overlay_tool_click',
  EXTENSION_OVERLAY_NAV_CLICK: 'extension.overlay_nav_click',

  ACCOUNT_LOGOUT_CLICK: 'account.logout_click',
  // Settings > Secrets, discriminated by `action`; never carries a secret's name or value
  SECRETS_ACTION: 'secrets.action',
  CREDITS_PLAN_CLICK: 'credits_plan.click',
  MANAGE_PLAN_VIEW: 'manage_plan.view',
  MANAGE_PLAN_DISMISSED: 'manage_plan.dismissed',
  MANAGE_PLAN_REASON_TOGGLE: 'manage_plan.reason_toggle',
  MANAGE_PLAN_FEEDBACK_SUBMIT: 'manage_plan.feedback_submit',
  PAYWALL_INTRO_VIEW: 'paywall_intro.view',
  PAYWALL_INTRO_DISMISSED: 'paywall_intro.dismissed',
  PAYWALL_SURFACE_VIEW: 'paywall_surface.view',
  CHAT_SEND_BLOCKED: 'chat.send_blocked', // a send stopped by the paywall

  SIDEBAR_CHAT_CLICK: 'sidebar.chat_click',
  SIDEBAR_TOOL_CLICK: 'sidebar.tool_click',
  SIDEBAR_NAV_CLICK: 'sidebar.nav_click',
  CHATS_PAGE_SEARCHED: 'chats_page.searched', // once per search: the filter going from empty to typed
  WORKSPACE_FILE_ACTION: 'workspace.file_action',
  // Registration only; WORKSPACE_FILE_ACTION covers viewing, TOOL_CREATED covers builds
  ARTIFACT_REGISTERED: 'artifact.registered',
  SUGGESTIONS_REFRESH_CLICK: 'suggestions.refresh_click',
  // Interrupting a running scan; `source` says which surface it was clicked from
  SUGGESTIONS_SCAN_STOP_CLICK: 'suggestions.scan_stop_click',
  SUPPORT_FEEDBACK_SUBMITTED: 'support_feedback.submitted',
  SUPPORT_FEEDBACK_MODAL_DISMISSED: 'support_feedback.modal_dismissed',
  PRIVACY_POLICY_MODAL_OPENED: 'privacy_policy.modal_opened',
  PRIVACY_POLICY_LINK_CLICK: 'privacy_policy.link_click',
  SUGGESTION_BUILD_CLICK: 'suggestion.build_click',
  // A plan card's See plan / See details, Do it or Discuss, by `action` and `surface` (web only)
  SUGGESTION_PLAN_CLICK: 'suggestion.plan_click',
  // A link on an update card, or its Discuss, by `action` and `surface` (web only)
  SUGGESTION_UPDATE_CLICK: 'suggestion.update_click',
  SUGGESTION_MORE_CLICK: 'suggestion.more_click',
  // A Home suggestions tab (Tasks, Papers, Funding, Citations), by `tab` (web only)
  SUGGESTION_TAB_CLICK: 'suggestion.tab_click',
  // Chat opened from the daily-question email deep link (web only)
  DAILY_QUESTION_CHAT_OPENED: 'daily_question.chat_opened',
  EXAMPLE_WELCOME_PROMPT_CLICK: 'example_welcome.prompt_click',
  // Menu-driven uploads only; drag-drop uploads fire WORKSPACE_FILE_ACTION instead
  UPLOAD_CLICK: 'upload.click',

  SURVEY_VIEWED: 'survey.viewed',
  SURVEY_STEP_COMPLETED: 'survey.step_completed',
  SURVEY_COMPLETED: 'survey.completed',

  PAPER_CARD_PDF_CLICK: 'paper_card.pdf_click',
  ONBOARDING_PAPER_PANE_CLOSED: 'onboarding.paper_pane_closed',

  PAPER_MONITOR_FIND: 'paper_monitor.find_papers',
  PAPER_MONITOR_KEEP: 'paper_monitor.keep',
  PAPER_MONITOR_DISCARD: 'paper_monitor.discard',
  PAPER_MONITOR_MARK_TO_REVIEW: 'paper_monitor.mark_to_review',
  PAPER_MONITOR_SAVE_TO_WORKSPACE: 'paper_monitor.save_to_workspace',

  GRANTS_FINDER_SEARCH_NOW_CLICK: 'grants_finder.search_now_click',
  GRANTS_FINDER_SAVE_CLICK: 'grants_finder.save_click',
  GRANTS_FINDER_TAB_CLICK: 'grants_finder.tab_click',
  GRANTS_FINDER_NAV_CLICK: 'grants_finder.nav_click',

  DESKTOP_UPDATE_BANNER_SHOWN: 'desktop_update.banner_shown',
  DESKTOP_UPDATE_RELAUNCH_CLICKED: 'desktop_update.relaunch_clicked',
  DESKTOP_UPDATE_DISMISSED: 'desktop_update.dismissed',
  // Web-only: the admin page's own-account paywall controls, discriminated by `action`
  ADMIN_PAYWALL_CHANGED: 'admin.paywall_changed',
  // Web-only: the admin Academia profile page and its settings link, discriminated by `action`
  ADMIN_ACADEMIA_PROFILE_ACTION: 'admin.academia_profile_action',
  // Web-only: the admin page's own re-engagement flow switch, with `flow` (a flow key, or default)
  ADMIN_REENGAGEMENT_FLOW_CHANGED: 'admin.reengagement_flow_changed',
  // Web-only: the admin page's send of their own next re-engagement email, with their `flow`
  ADMIN_REENGAGEMENT_EMAIL_SEND: 'admin.reengagement_email_send',
  // Web-only: the admin page's real workspace scan, by `action` (run / view_scans)
  ADMIN_WORKSPACE_SCAN_ACTION: 'admin.workspace_scan_action',
} as const;

// Whitelist so a compromised or AI-authored mini-app bundle can't spoof other events
export const IFRAME_FORWARDABLE_EVENTS: ReadonlySet<CoScientistEventName> =
  new Set([
    EVENTS.TOOL_CLICK,
    EVENTS.TOOL_KEYBOARD_EVENT,
    EVENTS.TOOL_ERROR_VIEWED,
    EVENTS.TOOL_RUN_STARTED,
    EVENTS.TOOL_RUN_COMPLETE,
    EVENTS.TOOL_ERROR_SHOWN,
    EVENTS.TOOL_ERROR_FIX_CLICKED,
    EVENTS.TOOL_RECONNECT_SHOWN,
    EVENTS.TOOL_RECONNECT_RECOVERED,
    EVENTS.TOOL_DATA_UPLOADED,
    EVENTS.TOOL_OUTPUT_DOWNLOADED,
    EVENTS.TOOL_PARAMS_CHANGED,
  ]);

export type CoScientistEventName = (typeof EVENTS)[keyof typeof EVENTS];

export type EventMetadata = Record<string, unknown>;
